import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  MenuItem,
  Menu,
  Link,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { resetCommonSlice } from "../../store/common/CommonSlice";
import { RootState } from "../../store/store";
import { useTranslation } from "react-i18next";
import LanguageIcon from "@mui/icons-material/Language";
import i18n from "../../translator/i18next";
import LoginStyles from "./LoginStyles";
import RenderVideoBanner from "../RenderVideoBanner/RenderVideoBanner";

interface LoginOption {
  featureCode: string;
  featureLabel: string;
}

interface IProps {
  loginOptions: LoginOption[];
  defaultLoginType?: string;
  showLanguageSelector?: boolean;
  renderLoginForm: (loginType: string) => React.ReactNode;
  isCustomer: boolean;
}

const Login: React.FC<IProps> = ({
  loginOptions,
  defaultLoginType,
  showLanguageSelector = true,
  renderLoginForm,
  isCustomer,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);

  const [loginType, setLoginType] = useState<string>("");
  const [languageMenu, setLanguageMenu] = useState<null | HTMLElement>(null);

  const handleLoginWithChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newLoginType = event.target.value;
      setLoginType(newLoginType);
      dispatch(resetCommonSlice());
    },
    [dispatch]
  );

  useEffect(() => {
    setLoginType(
      defaultLoginType ||
        (loginOptions.length > 0 ? loginOptions[0].featureCode : "")
    );
  }, [loginOptions, defaultLoginType]);

  const handleLanguageClick = (event: React.MouseEvent<SVGSVGElement>) => {
    setLanguageMenu(event.currentTarget as unknown as HTMLElement);
  };

  const handleLanguageClose = () => {
    setLanguageMenu(null);
  };

  const handleLanguageSelect = (language: string) => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
      window.location.reload();
    }

    setLanguageMenu(null);
  };

  const loginContent = (
    <Box style={LoginStyles.loginCardContainer}>
      {showLanguageSelector && (
        <Box>
          <LanguageIcon
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
              fontSize: "30px",
              cursor: "pointer",
            }}
            onClick={handleLanguageClick}
          />
          <Menu
            anchorEl={languageMenu}
            open={Boolean(languageMenu)}
            onClose={handleLanguageClose}
            MenuListProps={{ "aria-labelledby": "language-button" }}
            disableScrollLock
          >
            <MenuItem
              id="loginpage_customer_selectlanguage_menu-item-english"
              data-testid="test-selectlanguage_menuitem-english"
              aria-label="Langugae English"
              selected={i18n.language === "en"}
              onClick={() => handleLanguageSelect("en")}
            >
              English
            </MenuItem>
            <MenuItem
              id="loginpage_customer_selectlanguage_menu-item-spanish"
              data-testid="test-selectlanguage_menuitem-spanish"
              aria-label="Langugae Spanish"
              selected={i18n.language === "es"}
              onClick={() => handleLanguageSelect("es")}
            >
              Español
            </MenuItem>
          </Menu>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          width: "80%",
          paddingY: 2,
        }}
      >
        <Box sx={LoginStyles.logo}>
          {uiSettings?.logo && (
            <img src={`data:image/png;base64,${uiSettings?.logo}`} alt="Logo" />
          )}
        </Box>

        <Typography sx={LoginStyles.loginMessage}>
          {isCustomer
            ? t("Welcome, please login to continue")
            : "Welcome, please login to continue"}
        </Typography>

        <FormControl>
          <RadioGroup
            value={loginType}
            sx={{ display: "flex", flexDirection: "row" }}
            onChange={handleLoginWithChange}
          >
            {loginOptions.map((option) => (
              <FormControlLabel
                id={`loginpage_${
                  isCustomer ? "customer" : "agent"
                }-radiobutton`}
                data-testid={`test-loginpage_${
                  isCustomer ? "customer" : "agent"
                }-radiobutton`}
                aria-label="select login option"
                key={option.featureCode}
                value={option.featureCode}
                control={<Radio />}
                label={
                  isCustomer ? t(option.featureLabel) : option.featureLabel
                }
              />
            ))}
          </RadioGroup>
        </FormControl>

        {renderLoginForm(loginType)}
        <Box
          sx={{
            alignSelf: "end",
            display: "flex",
            alignItems: "center",
            marginY: 1,
          }}
        >
          <Link
            id="loginpage_link-back-to-home"
            data-testid="test-loginpage_link-back-to-home"
            aria-label="Navigate to home page"
            href="/"
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {isCustomer ? t("Back to home") : "Back to home"}
          </Link>
        </Box>
      </Box>
    </Box>
  );

  const bannerContent = (
    <Box sx={LoginStyles.loginImage}>
      {uiSettings?.loginBannerType === "image" && (
        <img
          src={`data:image/png;base64,${uiSettings?.loginBannerImage}`}
          alt="Login Banner"
        />
      )}

      {uiSettings?.loginBannerType === "video" && (
        <RenderVideoBanner
          videoUrl={uiSettings?.loginBannerVideoUrl as string}
        />
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        ...LoginStyles.login,
        gridTemplateColumns: isCustomer ? "60% 40%" : "40% 60%",
      }}
    >
      {isCustomer ? (
        <>
          {bannerContent}
          {loginContent}
        </>
      ) : (
        <>
          {loginContent}
          {bannerContent}
        </>
      )}
    </Box>
  );
};

export default Login;
